import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import TimelineIcon from '@mui/icons-material/Timeline';
import { Box, Divider, useMediaQuery, useTheme, CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';
import Header from '../../components/Header';
import StatBox from '../../components/StatBox';
import axios from '../../utils/axiosInstance'; // axiosInstance를 import

const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isTablet = useMediaQuery('(max-width: 1024px)');
  
  // 데이터 상태 확장 - stats 필드 추가
  const [dashboardData, setDashboardData] = useState({
    agent_balance: 0,
    users_total_balance: 0,
    user_count: 0,
    child_agents_total_balance: 0,
    child_agent_count: 0,
    stats: {
      today: { bet_amount: 0, win_amount: 0, profit_amount: 0, bet_count: 0, win_count: 0 },
      yesterday: { bet_amount: 0, win_amount: 0, profit_amount: 0, bet_count: 0, win_count: 0 },
      month: { bet_amount: 0, win_amount: 0, profit_amount: 0, bet_count: 0, win_count: 0 }
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // API에서 데이터 가져오기
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/dashboard/data');
        
        if (response.data && response.data.success) {
          console.log('대시보드 데이터:', response.data.data);
          setDashboardData(response.data.data);
        } else {
          throw new Error(response.data.message || '데이터를 불러오는데 실패했습니다.');
        }
      } catch (err) {
        console.error('대시보드 데이터 가져오기 실패:', err);
        setError(err.message || '데이터를 불러오는데 문제가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
    
    // 1시간마다 자동 갱신
    const intervalId = setInterval(fetchDashboardData, 3600000);
    
    return () => clearInterval(intervalId);
  }, []);

  // 금액 포맷팅 함수
  const formatCurrency = (value) => {
    // 음수 값을 절대값으로 변환 (통계 테이블의 bet_amount는 음수로 저장됨)
    const absValue = Math.abs(parseFloat(value));
    return absValue.toLocaleString('ko-KR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' 원';
  };

  // 다크 모드 카드 스타일
  const cardStyle = {
    background: 'linear-gradient(145deg, rgba(35,35,50,0.9) 0%, rgba(35,45,65,0.85) 100%)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: '16px',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)',
    padding: isMobile ? '16px' : '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '115px',
    transition: 'all 0.3s ease-in-out',
    backdropFilter: 'blur(12px)',
    '&:hover': {
      boxShadow: '0 12px 24px rgba(0, 0, 0, 0.2)',
      transform: 'translateY(-4px)',
      background: 'linear-gradient(145deg, rgba(40,40,60,0.9) 0%, rgba(40,50,75,0.85) 100%)',
    },
  };

  const gridTemplate = isMobile ? 'repeat(1, 1fr)' : isTablet ? 'repeat(6, 1fr)' : 'repeat(12, 1fr)';
  
  // 금액 아이콘과 건수 아이콘 색상 구분
  const moneyIconStyle = { color: '#fbbf24', fontSize: 32 }; // 금액은 노란색
  const countIconStyle = { color: '#4ade80', fontSize: 32 }; // 건수는 녹색

  // 로딩 상태 표시
  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        sx={{ 
          background: 'linear-gradient(180deg, rgba(25,25,38,0.95) 0%, rgba(25,32,48,0.95) 100%)'
        }}
      >
        <CircularProgress sx={{ color: '#60a5fa' }} />
      </Box>
    );
  }

  // 에러 상태 표시
  if (error) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        flexDirection="column"
        sx={{ 
          background: 'linear-gradient(180deg, rgba(25,25,38,0.95) 0%, rgba(25,32,48,0.95) 100%)',
          color: '#f87171',
          padding: '20px',
          textAlign: 'center'
        }}
      >
        <Box sx={{ fontSize: '24px', fontWeight: 'bold', mb: 2 }}>데이터를 불러올 수 없습니다</Box>
        <Box sx={{ fontSize: '16px', opacity: 0.8 }}>{error}</Box>
      </Box>
    );
  }

  return (
    <Box p={isMobile ? '16px' : '24px'} sx={{ 
      background: 'linear-gradient(180deg, rgba(25,25,38,0.95) 0%, rgba(25,32,48,0.95) 100%)',
      minHeight: '100vh' 
    }}>
        {/* HEADER */}
        <Box 
          display="flex" 
          flexDirection="column" 
          gap={1}
          sx={{
            // h5 요소(subtitle)에 스타일 적용
            '& .MuiTypography-h5': {  
              fontSize: '16px !important',
              color: 'rgba(255,255,255,0.7) !important',
              lineHeight: 1.5,
              letterSpacing: '0.2px'
            },
            // h2 요소(title)에 스타일 적용
            '& .MuiTypography-h2': {
              fontSize: '32px !important',
              color: '#fff !important',
              fontWeight: 'bold !important',
              letterSpacing: '0.5px',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }
          }}
        >
          <Header
            title="대시보드"
            subtitle="금일 손익 금액, 금일 베팅 금액, 금일 베팅 건수, 금일 당첨 건수 등의 통계 데이터는 마지막 접속 시각을 기준으로 1시간 뒤에 새롭게 업데이트하여 반영됩니다."
          />
        </Box>

      {/* 카드 영역 */}
      <Box mt={5} display="grid" gridTemplateColumns={gridTemplate} gridAutoRows="160px" gap="20px">
        {[
          { 
            title: '에이전트 보유 금액', 
            subtitle: formatCurrency(dashboardData.agent_balance), 
            icon: <PointOfSaleIcon sx={moneyIconStyle} /> 
          },
          { 
            title: '유저 총 보유 금액', 
            subtitle: formatCurrency(dashboardData.users_total_balance), 
            icon: <PointOfSaleIcon sx={moneyIconStyle} /> 
          },
          { 
            title: '에이전트 유저 수', 
            subtitle: dashboardData.user_count + '명', 
            icon: <PersonAddIcon sx={countIconStyle} /> 
          },
          { 
            title: '하부 에이전트 총 보유 금액', 
            subtitle: formatCurrency(dashboardData.child_agents_total_balance), 
            icon: <PointOfSaleIcon sx={moneyIconStyle} /> 
          },
          { 
            title: '전체 하부 에이전트 수', 
            subtitle: dashboardData.child_agent_count + '명', 
            icon: <PersonAddIcon sx={countIconStyle} /> 
          },
        ].map((stat, i) => (
          <Box key={i} sx={{ ...cardStyle, gridColumn: isMobile ? 'span 1' : 'span 3' }}>
            <StatBox 
              title={stat.title} 
              subtitle={stat.subtitle} 
              icon={stat.icon}
              // StatBox 내부 스타일 오버라이드 (StatBox 컴포넌트가 props로 스타일 적용 가능한 경우)
              titleProps={{ sx: { color: '#fff', fontWeight: 600, fontSize: '16px' } }}
              subtitleProps={{ 
                sx: { 
                  color: stat.subtitle.includes('0.0') ? '#f87171' : '#4ade80', 
                  fontWeight: 700, 
                  fontSize: '18px' 
                } 
              }}
            />
          </Box>
        ))}
      </Box>

      <Divider sx={{ my: '40px', borderColor: 'rgba(255, 255, 255, 0.1)' }} />

      {/* SECOND HEADER */}
      <Box 
        mt={4}
        sx={{
          '& .MuiTypography-h2': {
            fontSize: '28px !important',
            color: '#fff !important',
            fontWeight: 'bold !important',
            letterSpacing: '0.5px',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)'
          }
        }}
      >
        <Header title="통계 데이터" />
      </Box>

      {/* 통계 데이터 카드 - API 데이터 연결 */}
      <Box mt={5} display="grid" gridTemplateColumns={gridTemplate} gridAutoRows="160px" gap="20px">
        {[
          { 
            title: '금일 베팅 금액',
            value: dashboardData.stats?.today?.bet_amount || 0,
            isMoney: true 
          },
          { 
            title: '금일 손익 금액',
            value: dashboardData.stats?.today?.profit_amount || 0,
            isMoney: true 
          },
          { 
            title: '금일 베팅 건수',
            value: dashboardData.stats?.today?.bet_count || 0,
            isMoney: false 
          },
          { 
            title: '금일 당첨 건수',
            value: dashboardData.stats?.today?.win_count || 0,
            isMoney: false 
          },
          { 
            title: '전일 베팅 금액',
            value: dashboardData.stats?.yesterday?.bet_amount || 0,
            isMoney: true 
          },
          { 
            title: '전일 손익 금액',
            value: dashboardData.stats?.yesterday?.profit_amount || 0,
            isMoney: true 
          },
          { 
            title: '전일 베팅 건수',
            value: dashboardData.stats?.yesterday?.bet_count || 0,
            isMoney: false 
          },
          { 
            title: '전일 당첨 건수',
            value: dashboardData.stats?.yesterday?.win_count || 0,
            isMoney: false 
          },
          { 
            title: '당월 베팅 금액',
            value: dashboardData.stats?.month?.bet_amount || 0,
            isMoney: true 
          },
          { 
            title: '당월 손익 금액',
            value: dashboardData.stats?.month?.profit_amount || 0,
            isMoney: true 
          },
          { 
            title: '당월 베팅 건수',
            value: dashboardData.stats?.month?.bet_count || 0,
            isMoney: false 
          },
          { 
            title: '당월 당첨 건수',
            value: dashboardData.stats?.month?.win_count || 0,
            isMoney: false 
          },
        ].map((stat, i) => {
          const icon = stat.isMoney ? 
            <PointOfSaleIcon sx={moneyIconStyle} /> : 
            <TimelineIcon sx={countIconStyle} />;
          
          const subtitle = stat.isMoney ? 
            formatCurrency(Math.abs(stat.value)) : 
            stat.value + '건';
          
          const isZero = stat.isMoney ? 
            Math.abs(stat.value) === 0 : 
            stat.value === 0;
          
          return (
            <Box key={i} sx={{ ...cardStyle, gridColumn: isMobile ? 'span 1' : 'span 3' }}>
              <StatBox 
                title={stat.title} 
                subtitle={subtitle} 
                icon={icon}
                // StatBox 내부 스타일 오버라이드
                titleProps={{ sx: { color: '#fff', fontWeight: 600, fontSize: '16px' } }}
                subtitleProps={{ 
                  sx: { 
                    color: isZero ? '#f87171' : '#4ade80', 
                    fontWeight: 700, 
                    fontSize: '18px' 
                  } 
                }}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default Dashboard;